<template>
    <div class="uploaded-knowledge-file-table">
      <input type="file" ref="replaceFileInput" accept=".pdf,.txt,.md,.html,.csv,.docx,.pptx,.xlsx,.json,.epub,.org,.rst" style="display:none" @change="onReplaceFileSelected" />
      <div class="header-table">
        <p>Documents</p>
      </div>
      <div class="table-wrapper">
        <table class="table-content">
          <thead>
            <tr>
              <th style="width: 30%">File Name</th>
              <th style="width: 15%">Status</th>
              <th style="width: 13%">Size</th>
              <th style="width: 22%">Uploaded On</th>
              <th style="width: 20%">Action</th>
            </tr>
          </thead>
          <tbody v-if="isLoadingFileList">
            <tr>
              <td colspan="3" class="table-loading-content">
                <div class="loading-section">
                  <div class="spinner-border" role="status"></div>
                </div>
              </td>
            </tr>
          </tbody>
          <tbody v-else-if="Array.isArray(knowledgeFileList) && knowledgeFileList.length > 0">
            <tr v-for="file in knowledgeFileList" :key="file.id">
              <td>
                <p>{{ file.filename }}</p>
              </td>
              <td>
                <div class="badge badge-pill badge-completed" v-if="file.status === 'success'">
                  <p>พร้อมใช้งาน</p>
                </div>
                <div class="badge badge-pill badge-pending" v-else-if="file.status === 'pending'">
                  <p>รอประมวลผล</p>
                </div>
                <div class="badge badge-pill badge-in_progress" v-else-if="file.status === 'parsing'">
                  <p>กำลัง parse</p>
                </div>
                <div class="badge badge-pill badge-in_progress" v-else-if="file.status === 'chunking'">
                  <p>กำลัง chunk</p>
                </div>
                <div class="badge badge-pill badge-in_progress" v-else-if="file.status === 'embedding'">
                  <p>กำลัง embed</p>
                </div>
                <div class="badge badge-pill badge-in_progress" v-else-if="file.status === 'augmenting'">
                  <p>กำลัง augment</p>
                </div>
                <div class="badge badge-pill badge-failed" v-else-if="file.status === 'failed'">
                  <p>ผิดพลาด</p>
                </div>
                <div class="badge badge-pill badge-unknown" v-else>
                  <p>ไม่ทราบสถานะ</p>
                </div>
              </td>
              <td>{{ formatSize(file.sizeInBytes) }}</td>
              <td>{{ formatDate(file.uploadedDate) }}</td>
              <td>
                <div class="action-icons">
                  <div
                    v-if="file.status === 'success'"
                    @click="handleDownloadKnowledgeFile(file)"
                    class="download-icon p-0"
                    title="Download">
                    <i class='bx bx-download'></i>
                  </div>
                  <div
                    v-if="file.status === 'success' && file.fieldDescriptionSupported"
                    @click="handleGetFieldDescriptions(file)"
                    class="field-icon p-0 ml-2"
                    title="Field Descriptions">
                    <i class='bx bx-detail'></i>
                  </div>
                  <div
                    v-if="file.status === 'success' || file.status === 'failed'"
                    @click="handleReplaceKnowledgeFile(file)"
                    class="replace-icon p-0 ml-2"
                    :class="{ 'is-disabled': isReplacingFile }"
                    title="Replace">
                    <i class='bx bx-transfer-alt'></i>
                  </div>
                  <div
                    v-if="file.status === 'success' || file.status === 'failed'"
                    @click="handleDeleteUploadedKnowledgeFile(file)"
                    class="delete-icon p-0 ml-2"
                    title="Delete">
                    <i class='bx bx-trash'></i>
                  </div>
                </div>
              </td>
            </tr>
          <tr>
            <td colspan="4">
              <infinite-loading
              @infinite="infiniteHandler"
              force-use-infinite-wrapper=".table-wrapper"
              ></infinite-loading>
            </td>
          </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td colspan="3">
                <div class="no-file-content">
                  <img src="../../../assets/images/no_file_found.png" alt="no file found" />
                  <div>
                    <p class="text-title">No file found</p>
                    <p class="text-subtitle">Please upload a file or try adjusting your search</p>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      <field-descriptions-modal
        v-if="showFieldDescriptionsModal && fieldDescriptionsFile"
        :file="fieldDescriptionsFile"
        :data="fieldDescriptionsData"
        :isSaving="isSavingFieldDescriptions"
        @closeModal="onFieldDescriptionsClose"
        @save="onFieldDescriptionsSave"
      />
      <!--Delete Confirmation Modal -->
      <div class="confirmation-delete-file-modal" v-if="showDeleteFileConfirmationModal">
        <div class="card-content">
          <div class="modal-loading-content" v-if="isDeletingFile">
            <div class="loading-section">
              <div class="spinner-border" role="status"></div>
            </div>
          </div>
          <div class="main-icon">
            <i class="bx bx-error"></i>
          </div>
          <div v-if="selectedFile.filename.length > 20" class="modal-title">
            <p>Delete</p>
            <p class=" truncated-text">{{selectedFile.filename}}</p>
          </div>
          <div v-else class="modal-title">
            <p>Delete <span>{{ selectedFile.filename }}</span></p>
          </div>
            <p class="modal-description truncated-text">Are you sure you want to delete {{selectedFile.filename}}? <br/>
              This action cannot be undone.</p>
          <div class="modal-confirm-delete-file-footer">
            <button class="modal-btn cancel-btn" @click="onCloseModal">Cancel</button>
            <button class="modal-btn confirm-btn" @click="onClickConfirmDeleteFile">Delete</button>
          </div>
        </div>
      </div>
    </div>
    </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import { DateTime } from 'luxon';
import InfiniteLoading from 'vue-infinite-loading';
import aliceApi from '../../../alice-api';
import { formatBytesHelper } from '../../../utiles/formatHelper';
import FieldDescriptionsModal from '../../Modals/FieldDescriptionsModal.vue';

export default {
  name: 'file-management-table',
  components: {
    InfiniteLoading,
    FieldDescriptionsModal,
  },
  props: {
    isReloadKnowledgeFileList: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showDeleteFileConfirmationModal: false,
      selectedFile: null,
      selectedFileForReplace: null,
      pageSize: 20,
      fileType: 'ai_assistant',
      after: null,
      isLoadingFileList: false,
      isDeletingFile: false,
      isReplacingFile: false,
      knowledgeFileList: [],
      showFieldDescriptionsModal: false,
      fieldDescriptionsFile: null,
      fieldDescriptionsData: null,
      isSavingFieldDescriptions: false,
    };
  },
  async mounted() {
    await this.initKnowledgeFileList();
  },
  beforeDestroy() {
    this.clearLocalKnowledgeFileList();
  },
  methods: {
    ...mapActions({
      loadKnowledgeFileList: 'knowledgeFile/loadKnowledgeFileList',
      deleteKnowledgeFile: 'knowledgeFile/deleteKnowledgeFile',
      clearLocalKnowledgeFileList: 'knowledgeFile/clearLocalKnowledgeFileList'
    }),
    async initKnowledgeFileList() {
      this.isLoadingFileList = true;
      this.knowledgeFileList = [];
      await this.getKnowledgeFileList(true);
      this.isLoadingFileList = false;
    },
    handleDeleteUploadedKnowledgeFile(file) {
      this.selectedFile = file;
      this.showDeleteFileConfirmationModal = true;
    },
    onCloseModal() {
      this.showDeleteFileConfirmationModal = false;
    },
    async onClickConfirmDeleteFile() {
      this.isDeletingFile = true;
      try {
        await this.deleteKnowledgeFile({ id: this.selectedFile.id, fileType: this.fileType });
      } catch (error) {
        console.error('Error deleting file', error);
        this.$buefy.toast.open({
          type: 'is-danger',
          message: 'Cannot delete file, Please try again',
          position: 'is-bottom',
          duration: 3000,
        });
        return;
      } finally {
        this.isDeletingFile = false;
        this.onCloseModal();
      }
      this.$buefy.toast.open({
        type: 'is-success',
        message: this.$sanitize(`${this.selectedFile.filename} has been successfully deleted`),
        position: 'is-bottom',
        duration: 3000,
      });
      await this.initKnowledgeFileList();
    },
    async handleGetFieldDescriptions(file) {
      try {
        const response = await aliceApi.getKnowledgeFileFieldDescriptions(file.id, this.fileType);
        this.fieldDescriptionsFile = file;
        this.fieldDescriptionsData = response.data.data;
        this.showFieldDescriptionsModal = true;
      } catch (error) {
        console.error('Error fetching field descriptions', error);
      }
    },
    async onFieldDescriptionsSave(formData) {
      if (this.isSavingFieldDescriptions) return;
      this.isSavingFieldDescriptions = true;
      try {
        await aliceApi.updateKnowledgeFileFieldDescriptions(
          this.fieldDescriptionsFile.id,
          this.fileType,
          formData,
        );
        this.$buefy.toast.open({
          type: 'is-success',
          message: 'Field descriptions updated successfully.',
          position: 'is-bottom',
          duration: 3000,
        });
        this.showFieldDescriptionsModal = false;
      } catch (error) {
        console.error('Error updating field descriptions', error);
        this.$buefy.toast.open({
          type: 'is-danger',
          message: 'Cannot update field descriptions, Please try again',
          position: 'is-bottom',
          duration: 3000,
        });
      } finally {
        this.isSavingFieldDescriptions = false;
      }
    },
    onFieldDescriptionsClose() {
      this.showFieldDescriptionsModal = false;
      this.fieldDescriptionsFile = null;
      this.fieldDescriptionsData = null;
    },
    handleReplaceKnowledgeFile(file) {
      if (this.isReplacingFile) return;
      this.selectedFileForReplace = file;
      this.$refs.replaceFileInput.value = '';
      this.$refs.replaceFileInput.click();
    },
    async onReplaceFileSelected(event) {
      const newFile = event.target.files[0];
      if (!newFile) return;
      this.isReplacingFile = true;
      try {
        const formData = new FormData();
        formData.append('file', newFile);
        formData.append('type', this.fileType);
        await aliceApi.replaceKnowledgeFile(this.selectedFileForReplace.id, formData);
        this.$buefy.toast.open({
          type: 'is-success',
          message: this.$sanitize(`${this.selectedFileForReplace.filename} has been successfully replaced`),
          position: 'is-bottom',
          duration: 3000,
        });
        await this.initKnowledgeFileList();
      } catch (error) {
        console.error('Error replacing file', error);
        this.$buefy.toast.open({
          type: 'is-danger',
          message: 'Cannot replace file, Please try again',
          position: 'is-bottom',
          duration: 3000,
        });
      } finally {
        this.isReplacingFile = false;
        this.selectedFileForReplace = null;
      }
    },
    async handleDownloadKnowledgeFile(file) {
      try {
        const response = await aliceApi.downloadKnowledgeFile(file.id, this.fileType);
        const disposition = response.headers['content-disposition'];
        const match = disposition && disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
        const suggestedName = match ? decodeURIComponent(match[1]) : file.filename;
        const url = URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = suggestedName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading file', error);
        this.$buefy.toast.open({
          type: 'is-danger',
          message: 'Cannot download file, Please try again',
          position: 'is-bottom',
          duration: 3000,
        });
      }
    },
    formatDate(unixTimeMilliseconds) {
      return DateTime.fromMillis(unixTimeMilliseconds).setZone('Asia/Bangkok').toFormat('dd/MM/yyyy HH:mm:ss');
    },
    formatSize(bytes) {
      if (!bytes) return '-';
      return formatBytesHelper(bytes);
    },
    async getKnowledgeFileList(isInit = false) {
      const size = this.pageSize;
      const type = this.fileType;
      if (isInit) {
        this.after = null;
      } else {
        this.after = this.knowledgeFileData.after ?? null;
      }
      await this.loadKnowledgeFileList({ size, type, after: this.after });
      this.knowledgeFileList = this.knowledgeFileList.concat(this.knowledgeFileData.content);
      this.after = this.knowledgeFileData.after;
    },

    async infiniteHandler($state) {
      if (this.knowledgeFileData.isLast) {
        // TODO Add more condition for this `if`
        // Why?
        // Based on backend implementation when you got empty content `isFirst` can be `true` too.
        // This can lead to showing invalid wording but as we already check for
        // `this.knowledgeFileList` length it will cover this case
        if (this.knowledgeFileData.isFirst) {
          $state.loaded();
        }
        $state.complete();
        return;
      }
      try {
        await this.getKnowledgeFileList();
        $state.loaded();
      } catch (error) {
        $state.error();
        console.error('Error loading more files', error);
      }
    },
  },
  computed: {
    ...mapGetters({
      knowledgeFileData: 'knowledgeFile/knowledgeFileData'
    })
  },
  watch: {
    isReloadKnowledgeFileList: {
      handler(newValue) {
        if (newValue) {
          this.initKnowledgeFileList();
          this.$emit('on-reload-knowledge-file-list', false);
        }
      },
    },
  },
};
</script>
<style lang="scss" scoped>
.uploaded-knowledge-file-table{
  height: 100%;
  p{
    margin-bottom: 0px;
  }
  i{
    font-size: 16px;
  }
  background-color: #fff;

  .table-wrapper {
    padding: 0px 20px;
    max-height: 400px;
    overflow: auto;
  }
  .header-table{
    padding: 12px 20px;
    font-size: 16px;
    font-weight: 500;
    line-height: 19.2px;
    color: #111B49;
    display: flex;
    justify-content: space-between;
    p {
        margin-bottom: 0px
    }
  }
  .table-content{
    width: 100%;
    color: #3D4B65;
    letter-spacing: 0%;
    table-layout: fixed;
    height: 100%;
    .action-icons{
      display: flex;
      align-items: center;
      white-space: nowrap;
      flex-wrap: nowrap;
    }
    .download-icon{
      color: #868BA6;
      cursor: pointer;
    }
    .field-icon{
      color: #868BA6;
      cursor: pointer;
    }
    .replace-icon{
      color: #868BA6;
      cursor: pointer;
      &.is-disabled{
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
      }
    }
    .delete-icon{
      color: #868BA6;
      cursor: pointer;
    }
    thead {
      position: sticky;
      top: 0px;
      tr {
        th {
          background-color: #FAFAFC;
          padding: 12px 16px;
          font-size: 14px;
          font-weight: 500;
          text-align: left;
          margin-top: auto;
          margin-bottom: auto;
          line-height: 16.41px;
          border-bottom: 1px solid #9EA5B2;
        }
      }
    }
    tbody{
      tr{
        td{
          padding: 16px 16px 12px 16px;
          font-weight: 400;
          font-size: 14px;
          line-height: 20px;
        }
      }
      tr:not(:last-child){
        td{
          border-bottom: 1px solid #E7E8EE;
        }
      }
      .badge-completed{
        background-color: #50CD89;
        color: #fff;
      }
      .badge-in_progress{
        background-color: #59CBE8;
        color: #fff;
      }
      .badge-pending{
        background-color: #868BA6;
        color: #fff;
      }
      .badge-failed{
        background-color: #EF6767;
        color: #fff;
      }
      .badge-unknown{
        background-color: #C4C4C4;
        color: #fff;
      }
    }
  }
  .confirmation-delete-file-modal{
  position: absolute;
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100vh;
  top: 0px;
  left: 0;
  z-index: 1040;
  background-color: rgba(0, 0, 0, 0.5);
  .card-content{
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: #fff;
    width: 500px;
    height: fit-content;
    border-radius: 16px;
    margin-top: 30px;
    text-align: center;
    position: relative;
    padding: 24px;
    .modal-title{
      color: #0D173C;
      font-size: 20px;
      font-weight:500;
      line-height: 24px;
      margin-top: 16px;
      width: 400px;
    }
    .modal-description{
      color: #646A8C;
      font-size: 14px;
      font-weight: 400;
      line-height: 21px;
      width: 100%;
      margin-top: 16px;
    }
    .truncated-text {
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .modal-confirm-delete-file-footer{
      display: flex;
      justify-content: center;
      margin-top: 24px;
    }
    .modal-btn{
      border-radius: 4px;
      padding: 8px 16px;
      border: unset;
      margin: 4px;
      &.cancel-btn{
        color: #F44336;
        background-color: #fff;
        border: 1px solid #F44336;
        &:hover{
          cursor: pointer;
        }
      }
      &.confirm-btn{
        color: #fff;
        background-color: #F44336;
        &:hover{
          cursor: pointer;
        }
      }
      &:focus{
        outline: 0px;
      }
    }
    .main-icon{
      i{
        font-size: 72px;
        color: #F44336;
      }
    }
  }
}
  .no-file-content{
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    padding: 80px;
    color: #666D92;
    img{
      width: 80px;
      height: 91px;
      margin-bottom: 18px;
    }
    .text-title{
      font-weight: 500;
      font-size: 16px;
      line-height: 19.2px;
      letter-spacing: 0px;
    }
    .text-subtitle{
      font-weight: 400;
      font-size: 14px;
      line-height: 21px;
      letter-spacing: 0px;
    }
  }
  .table-loading-content{
    height: 350px;
  }
  .loading-section{
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
  }
  .modal-loading-content{
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
    background-color: rgba(255, 255, 255, 0.5);
    position: absolute;
    top: 0px;
  }
}
</style>
